import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as LayoutComponents from '../Shared/LayoutComponents.js'
import ProfileTypeTab from './ProfileTypeTab.js';
import NotesTab from './NotesTab.js';
import SystematicReviewsTab from './SystematicReviewsTab.js';
import AddProfilesPopup from './AddProfilesPopup.js';
import WatchlistContentChart from './WatchlistContentChart.js';
import { getProfileTypeConfig, getProfileTypeLabel } from './profileTypeCatalog.js';
import RowActionMenu from './RowActionMenu.js';
import ConfirmRemovePopup from './ConfirmRemovePopup.js';
import { CancelButton, SaveButton, PrimaryPillButton, ActionButtonsRow } from './ActionButtons.js';
import LoadingSpinnerImage from '../Shared/LoadingSpinnerImage.js';
import '../Shared/Style.css';
import './App.css';

const formatDateInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const oneMonthBefore = (date) => {
    const previous = new Date(date);
    previous.setMonth(previous.getMonth() - 1);
    if (previous.getDate() !== date.getDate())
        previous.setDate(0);
    return previous;
};

const oneYearBefore = (date) => {
    const previous = new Date(date);
    previous.setFullYear(previous.getFullYear() - 1);
    return previous;
};

const STATS_RANGE_PRESETS = {
    "last-week": {
        label: "Last week",
        getDates: (today) => ({
            snapshotDate: formatDateInput(today),
            compareDate: formatDateInput(addDays(today, -7)),
        }),
    },
    "last-month": {
        label: "Last month",
        getDates: (today) => ({
            snapshotDate: formatDateInput(today),
            compareDate: formatDateInput(oneMonthBefore(today)),
        }),
    },
    "last-year": {
        label: "Last year",
        getDates: (today) => ({
            snapshotDate: formatDateInput(today),
            compareDate: formatDateInput(oneYearBefore(today)),
        }),
    },
};

const ItemDetailsPane = ({ globalContext }) => {

    const AppContext = globalContext;
    const [isSavingItem, setIsSavingItem] = useState(false);
    const [isShowingSaveMessage, setIsShowingSaveMessage] = useState(false);
    const [activeSection, setActiveSection] = useState("information");
    const [itemDetailsPaneActive, setItemDetailsPaneActive] = useState(AppContext.isPaneOpen);
    const [itemDetailsPaneOpenEffect, setItemDetailsPaneOpenEffect] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errorCurrentItem, setErrorCurrentItem] = useState(false);
    const [showItemContextMenu, setShowItemContextMenu] = useState(false);
    const [isUpdatingTypes, setIsUpdatingTypes] = useState(false);
    const [typeError, setTypeError] = useState(null);
    const [isAddProfilesOpen, setIsAddProfilesOpen] = useState(false);
    const [sectionMenuType, setSectionMenuType] = useState(null);
    const [sectionMenuAnchor, setSectionMenuAnchor] = useState(null);
    const [columnsDialogType, setColumnsDialogType] = useState(null);
    const [pendingRemoveType, setPendingRemoveType] = useState(null);
    const [removeTypeError, setRemoveTypeError] = useState(null);
    const [profilesRefreshKey, setProfilesRefreshKey] = useState(0);
    const initialPresetDates = STATS_RANGE_PRESETS["last-week"].getDates(new Date());
    const [statsRangePreset, setStatsRangePreset] = useState("last-week");
    const [snapshotDate, setSnapshotDate] = useState(initialPresetDates.snapshotDate);
    const [compareDate, setCompareDate] = useState(initialPresetDates.compareDate);
    const [compareEnabled, setCompareEnabled] = useState(true);
    const [statsVersion, setStatsVersion] = useState(0);
    const [isUpdatingStats, setIsUpdatingStats] = useState(false);
    const [bottomSpacerHeight, setBottomSpacerHeight] = useState(0);

    const contextMenuButton = useRef(null);
    const contextMenu = useRef(null);
    const contentScrollRef = useRef(null);
    const sectionRefs = useRef({});
    const isProgrammaticScroll = useRef(false);
    const pendingStatsTypesRef = useRef(new Set());

    const profileTypes = AppContext.currentItem?.ProfileTypes || [];

    const statsCapableTypes = useMemo(() => {
        return profileTypes.filter((type) => {
            const config = getProfileTypeConfig(type);
            return !!(config?.statsEndpoint && (config.statsFields || []).length > 0);
        });
    }, [profileTypes.join("|")]);

    const showSharedStatsBar = statsCapableTypes.length > 0;

    const navItems = [
        { id: "information", title: "Information" },
        ...profileTypes.map((type) => ({ id: `type:${type}`, title: getProfileTypeLabel(type) })),
        { id: "notes", title: "Intel Blocks" },
        { id: "systematic-reviews", title: "Systematic Reviews" },
    ];

    useEffect(() => {
        if (AppContext.isPaneOpen == true) {
            setItemDetailsPaneActive(true);
            setTimeout(() => { setItemDetailsPaneOpenEffect(true); }, 100);
        }
        else {
            setItemDetailsPaneOpenEffect(false);
            setTimeout(() => { setItemDetailsPaneActive(false); }, 300);
        }
    }, [AppContext.isPaneOpen]);

    const reinitialize = () => {
        setActiveSection("information");
        setIsSavingItem(false);
        AppContext.setIsShowingDeleteConfirmation(false);
        setIsShowingSaveMessage(false);
        setIsEditMode(false);
        setErrorCurrentItem(false);
        setTypeError(null);
        setIsAddProfilesOpen(false);
        setSectionMenuType(null);
        setSectionMenuAnchor(null);
        setColumnsDialogType(null);
        setPendingRemoveType(null);
        setRemoveTypeError(null);
        const presetDates = STATS_RANGE_PRESETS["last-week"].getDates(new Date());
        setStatsRangePreset("last-week");
        setSnapshotDate(presetDates.snapshotDate);
        setCompareDate(presetDates.compareDate);
        setCompareEnabled(true);
        setStatsVersion(0);
        setIsUpdatingStats(false);
        pendingStatsTypesRef.current = new Set();
        if (contentScrollRef.current)
            contentScrollRef.current.scrollTop = 0;
    };

    const applyStatsRangePreset = (preset) => {
        setStatsRangePreset(preset);
        if (preset === "custom")
            return;

        const config = STATS_RANGE_PRESETS[preset];
        if (!config)
            return;

        const dates = config.getDates(new Date());
        setSnapshotDate(dates.snapshotDate);
        setCompareDate(dates.compareDate);
        setCompareEnabled(true);
    };

    const handleStatsUpdateComplete = useCallback((profileType) => {
        if (!pendingStatsTypesRef.current.has(profileType))
            return;
        pendingStatsTypesRef.current.delete(profileType);
        if (pendingStatsTypesRef.current.size === 0)
            setIsUpdatingStats(false);
    }, []);

    const startStatsUpdate = () => {
        if (isUpdatingStats || statsCapableTypes.length === 0)
            return;

        pendingStatsTypesRef.current = new Set(statsCapableTypes);
        setIsUpdatingStats(true);
        setStatsVersion((version) => version + 1);
    };

    useEffect(() => {
        if (itemDetailsPaneActive)
            reinitialize();
    }, [itemDetailsPaneActive, AppContext.currentItem?.Id]);

    useEffect(() => {
        if (!itemDetailsPaneOpenEffect)
            setShowItemContextMenu(false);
    }, [itemDetailsPaneOpenEffect]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if ((contextMenuButton.current && contextMenuButton.current.contains(e.target)) ||
                (contextMenu.current && contextMenu.current.contains(e.target)))
                return;
            setShowItemContextMenu(false);
            AppContext.setIsShowingDeleteConfirmation(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const setSectionRef = useCallback((id, node) => {
        if (node)
            sectionRefs.current[id] = node;
        else
            delete sectionRefs.current[id];
    }, []);

    const updateActiveFromScroll = useCallback(() => {
        if (isProgrammaticScroll.current)
            return;

        const container = contentScrollRef.current;
        if (!container || navItems.length === 0)
            return;

        const containerTop = container.getBoundingClientRect().top;
        const offset = 48;
        let current = navItems[0].id;

        for (const item of navItems) {
            const el = sectionRefs.current[item.id];
            if (!el)
                continue;
            const top = el.getBoundingClientRect().top - containerTop;
            if (top <= offset)
                current = item.id;
        }

        setActiveSection((prev) => (prev === current ? prev : current));
    }, [navItems.map((n) => n.id).join("|")]);

    const updateBottomSpacer = useCallback(() => {
        const container = contentScrollRef.current;
        if (!container || navItems.length === 0) {
            setBottomSpacerHeight(0);
            return;
        }

        const lastId = navItems[navItems.length - 1].id;
        const lastEl = sectionRefs.current[lastId];
        if (!lastEl) {
            setBottomSpacerHeight(0);
            return;
        }

        // Enough room so the last section heading can reach the scroll-spy offset.
        const offset = 48;
        const needed = Math.max(0, container.clientHeight - lastEl.offsetHeight - offset);
        setBottomSpacerHeight((prev) => (prev === needed ? prev : needed));
    }, [navItems.map((n) => n.id).join("|")]);

    useEffect(() => {
        const container = contentScrollRef.current;
        if (!container)
            return;

        const onScroll = () => updateActiveFromScroll();
        container.addEventListener("scroll", onScroll, { passive: true });
        updateActiveFromScroll();
        return () => container.removeEventListener("scroll", onScroll);
    }, [updateActiveFromScroll, itemDetailsPaneActive, AppContext.currentItem?.Id, profilesRefreshKey, bottomSpacerHeight]);

    useEffect(() => {
        const frame = window.requestAnimationFrame(() => updateBottomSpacer());
        const container = contentScrollRef.current;
        if (!container)
            return () => window.cancelAnimationFrame(frame);

        const lastId = navItems[navItems.length - 1]?.id;
        const lastEl = lastId ? sectionRefs.current[lastId] : null;

        const observer = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(() => updateBottomSpacer())
            : null;
        observer?.observe(container);
        if (lastEl)
            observer?.observe(lastEl);
        window.addEventListener("resize", updateBottomSpacer);

        return () => {
            window.cancelAnimationFrame(frame);
            observer?.disconnect();
            window.removeEventListener("resize", updateBottomSpacer);
        };
    }, [updateBottomSpacer, itemDetailsPaneActive, AppContext.currentItem?.Id, profilesRefreshKey, navItems.map((n) => n.id).join("|")]);

    const scrollToSection = (id) => {
        const el = sectionRefs.current[id];
        const container = contentScrollRef.current;
        if (!el || !container)
            return;

        setActiveSection(id);
        isProgrammaticScroll.current = true;
        const containerTop = container.getBoundingClientRect().top;
        const elTop = el.getBoundingClientRect().top;
        const nextTop = container.scrollTop + (elTop - containerTop) - 8;
        container.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });

        window.setTimeout(() => {
            isProgrammaticScroll.current = false;
            updateActiveFromScroll();
        }, 450);
    };

    const closePane = () => {
        if (AppContext.closePane)
            AppContext.closePane();
    };

    const cancelEdit = () => {
        AppContext.setCurrentItem(AppContext.originalItem);
        setIsEditMode(false);
        setErrorCurrentItem(false);
    };

    const applyWatchlistResult = (result) => {
        if (!result?.Id)
            return;
        const updatedDataArray = AppContext.data.map((item) => item.Id === result.Id ? result : item);
        AppContext.setData(updatedDataArray);
        AppContext.setCurrentItem(result);
        AppContext.setOriginalItem(result);
    };

    const saveCurrentItem = () => {
        setErrorCurrentItem(false);
        if (!AppContext.currentItem?.Name || AppContext.currentItem.Name.trim() === "") {
            setErrorCurrentItem("Name is required");
            return;
        }

        setIsSavingItem(true);
        setIsShowingSaveMessage(false);

        const formData = new FormData();
        formData.append("Id", AppContext.currentItem.Id);
        formData.append("Name", AppContext.currentItem.Name.trim());
        formData.append("Description", AppContext.currentItem.Description || "");
        formData.append("AlertsEnabled", AppContext.currentItem.AlertsEnabled ? "true" : "false");
        // Alert users are self-activated — do not sync membership from this editor

        fetch(AppContext.dataUrl, { method: "PUT", body: formData })
            .then(async (response) => {
                if (!response.ok) {
                    const message = await response.text();
                    throw new Error(message || "Could not save watchlist");
                }
                return response.json();
            })
            .then((result) => {
                applyWatchlistResult(result);
                setIsShowingSaveMessage(true);
                setIsEditMode(false);
            })
            .catch((err) => setErrorCurrentItem(err.message || "Could not save watchlist"))
            .finally(() => setIsSavingItem(false));
    };

    const refreshCurrentItem = () => {
        fetch(`/umbraco/api/WatchlistsApi/Watchlist?Id=${AppContext.currentItem.Id}`)
            .then((response) => {
                if (!response.ok) throw new Error("Could not refresh watchlist");
                return response.json();
            })
            .then((result) => applyWatchlistResult(result))
            .catch(() => { /* ignore */ });
    };

    const toggleAlertsEnabled = () => {
        if (!AppContext.currentItem)
            return;

        const previousItem = AppContext.currentItem;
        const nextEnabled = !previousItem.AlertsEnabled;
        AppContext.setCurrentItem({
            ...previousItem,
            AlertsEnabled: nextEnabled,
        });

        // Persist immediately so it works outside edit mode too
        const formData = new FormData();
        formData.append("workspaceId", previousItem.Id);
        formData.append("enabled", nextEnabled ? "true" : "false");
        fetch("/umbraco/api/WatchlistsApi/WatchlistAlertsEnabled", { method: "POST", body: formData })
            .then(async (response) => {
                if (!response.ok)
                    throw new Error(await response.text() || "Could not update alerts");
                return response.json();
            })
            .then((result) => {
                if (result && typeof result.AlertsEnabled === "boolean") {
                    AppContext.setCurrentItem({
                        ...previousItem,
                        ...result,
                        AlertsEnabled: result.AlertsEnabled,
                    });
                }
            })
            .catch(() => {
                AppContext.setCurrentItem(previousItem);
            });
    };

    const closeSectionMenu = () => {
        setSectionMenuType(null);
        setSectionMenuAnchor(null);
    };

    const toggleSectionMenu = (profileType, event) => {
        event.preventDefault();
        event.stopPropagation();
        if (sectionMenuType === profileType) {
            closeSectionMenu();
            return;
        }
        setSectionMenuAnchor(event.currentTarget);
        setSectionMenuType(profileType);
    };

    const openColumnsDialog = (profileType) => {
        closeSectionMenu();
        setColumnsDialogType(profileType);
    };

    const askRemoveProfileType = (profileType) => {
        closeSectionMenu();
        setRemoveTypeError(null);
        setPendingRemoveType(profileType);
    };

    const removeProfileType = () => {
        const profileType = pendingRemoveType;
        if (!profileType || isUpdatingTypes)
            return;

        setIsUpdatingTypes(true);
        setRemoveTypeError(null);
        fetch(`/umbraco/api/WatchlistsApi/WatchlistProfileTypes?workspaceId=${AppContext.currentItem.Id}&profileType=${encodeURIComponent(profileType)}`, { method: "DELETE" })
            .then((response) => {
                if (!response.ok) throw new Error("Could not remove profile type");
                if (activeSection === `type:${profileType}`)
                    setActiveSection("information");
                setPendingRemoveType(null);
                refreshCurrentItem();
            })
            .catch((err) => setRemoveTypeError(err.message))
            .finally(() => setIsUpdatingTypes(false));
    };

    const handleProfilesChanged = () => {
        refreshCurrentItem();
        setProfilesRefreshKey((key) => key + 1);
    };

    const handleProfileTypeEmptied = (profileType) => {
        if (activeSection === `type:${profileType}`)
            setActiveSection("information");
        refreshCurrentItem();
        setProfilesRefreshKey((key) => key + 1);
    };

    if (!itemDetailsPaneActive)
        return null;

    const alertsEnabled = !!AppContext.currentItem?.AlertsEnabled;

    return (
        <div style={{ height: "100vh", overflow: "hidden", backgroundColor: "#f7fafc" }} className={`slide-pane-full-width ${itemDetailsPaneOpenEffect ? "open" : ""}`}>
            <div className="content" style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <p style={{ color: "#555", fontSize: "11px", flexShrink: 0 }}>
                    <span className="breadcrumb-link" style={{ marginRight: "5px" }} onClick={() => closePane()}>Watchlists</span>
                    {(AppContext.currentItem === null || AppContext.isLoadingItemDetails) ? null : (
                        <> &gt; <span style={{ marginLeft: "5px" }}>{AppContext.currentItem.Name}</span></>
                    )}
                </p>

                <div className="item-details-content" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                    <div className="item-details-content-tabs" style={{ maxHeight: "100%", overflowY: "auto" }}>
                        <div className="nav" style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            {navItems.map((item) => (
                                <a
                                    key={item.id}
                                    onClick={() => scrollToSection(item.id)}
                                    className={`nav-item item-details-menu-tab ${activeSection === item.id ? "active" : ""}`}
                                >
                                    {item.title}
                                </a>
                            ))}
                        </div>
                    </div>

                    <div
                        className="item-details-content-content watchlists-details-scroll"
                        ref={contentScrollRef}
                        style={{ height: "100%", maxHeight: "none", overflowY: "auto" }}
                    >
                        {(AppContext.currentItem === null || AppContext.isLoadingItemDetails) ? (
                            <LayoutComponents.PageLoading />
                        ) : (
                            <>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                        <h2 style={{ margin: 0, fontSize: "24px", fontWeight: 700, color: "#1a202c", display: "flex", alignItems: "center", gap: "10px" }}>
                                            <i className="fas fa-binoculars" style={{ color: "#2b6cb0" }} />
                                            Watchlist
                                        </h2>
                                        <span style={{ fontSize: "16px", color: "#4a5568", fontWeight: 600, background: "#edf2f7", padding: "4px 12px", borderRadius: "6px" }}>
                                            {AppContext.currentItem.Name}
                                        </span>
                                        <button
                                            type="button"
                                            className={`watchlists-alerts-bell ${alertsEnabled ? "enabled" : "disabled"}`}
                                            title={alertsEnabled ? "Click to disable alerts" : "Click to enable alerts"}
                                            onClick={toggleAlertsEnabled}
                                        >
                                            <i className={alertsEnabled ? "fas fa-bell" : "fas fa-bell-slash"} />
                                        </button>
                                    </div>

                                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                        <PrimaryPillButton onClick={() => setIsAddProfilesOpen(true)}>
                                            <i className="fas fa-plus pointer g-mr-8"></i>Add Profile
                                        </PrimaryPillButton>
                                        {isEditMode ? (
                                            <ActionButtonsRow isLoading={isSavingItem}>
                                                <CancelButton onClick={cancelEdit} />
                                                <SaveButton onClick={saveCurrentItem} />
                                            </ActionButtonsRow>
                                        ) : isShowingSaveMessage ? (
                                            <span className="save-message-shown">Save successful!</span>
                                        ) : null}
                                        <div style={{ position: "relative" }}>
                                            <button type="button" className="btn btn-outline-secondary" ref={contextMenuButton} onClick={() => setShowItemContextMenu(!showItemContextMenu)} style={{ border: "1px solid #cbd5e0", background: "white", borderRadius: "6px", padding: "6px 10px", cursor: "pointer", color: "#4a5568" }}>
                                                <i className="fas fa-ellipsis-v" />
                                            </button>
                                            {showItemContextMenu && (
                                                <div ref={contextMenu} style={{ position: "absolute", right: 0, top: "110%", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "6px", minWidth: "140px", zIndex: 20, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
                                                    {!isEditMode && (
                                                        <button
                                                            type="button"
                                                            style={{ width: "100%", textAlign: "left", padding: "8px 12px", border: "none", background: "none", cursor: "pointer", color: "#2d3748" }}
                                                            onClick={() => {
                                                                setShowItemContextMenu(false);
                                                                setIsEditMode(true);
                                                            }}
                                                        >
                                                            Edit
                                                        </button>
                                                    )}
                                                    <button type="button" style={{ width: "100%", textAlign: "left", padding: "8px 12px", border: "none", background: "none", cursor: "pointer", color: "#e53e3e" }} onClick={() => AppContext.deleteCurrentItem && AppContext.deleteCurrentItem({})}>
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <button type="button" onClick={() => closePane()} title="Close" style={{ border: "1px solid #cbd5e0", background: "white", borderRadius: "6px", padding: "6px 10px", cursor: "pointer", color: "#4a5568" }}>
                                            <i className="fas fa-times" />
                                        </button>
                                    </div>
                                </div>

                                {errorCurrentItem && <div style={{ color: "#e53e3e", marginBottom: "12px" }}>{errorCurrentItem}</div>}
                                {typeError && <div style={{ color: "#e53e3e", marginBottom: "12px" }}>{typeError}</div>}

                                <section
                                    className="watchlists-details-section"
                                    ref={(node) => setSectionRef("information", node)}
                                    id="watchlist-section-information"
                                >
                                    <h3 className="watchlists-details-section-title">Information</h3>
                                    <LayoutComponents.InfoBlock>
                                        <div className="watchlists-information-layout">
                                            <div className="watchlists-information-fields" style={{ display: "grid", gap: "16px" }}>
                                            <div>
                                                <label style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}>Name</label>
                                                {isEditMode ? (
                                                    <input type="text" className="form-control" value={AppContext.currentItem.Name || ""} onChange={(e) => AppContext.setCurrentItem({ ...AppContext.currentItem, Name: e.target.value })} style={{ width: "100%" }} />
                                                ) : (
                                                    <div>{AppContext.currentItem.Name}</div>
                                                )}
                                            </div>
                                            <div>
                                                <label style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}>Description</label>
                                                {isEditMode ? (
                                                    <textarea className="form-control" rows={4} value={AppContext.currentItem.Description || ""} onChange={(e) => AppContext.setCurrentItem({ ...AppContext.currentItem, Description: e.target.value })} style={{ width: "100%" }} />
                                                ) : (
                                                    <div style={{ whiteSpace: "pre-wrap" }}>{AppContext.currentItem.Description || "---"}</div>
                                                )}
                                            </div>
                                            </div>
                                            <div className="watchlists-information-chart">
                                                <WatchlistContentChart
                                                    contentCounts={AppContext.currentItem.ContentCounts || []}
                                                    onCategoryClick={scrollToSection}
                                                />
                                            </div>
                                        </div>
                                    </LayoutComponents.InfoBlock>
                                </section>

                                {profileTypes.length > 0 && showSharedStatsBar && (
                                    <div className="watchlists-shared-stats-bar">
                                        <div style={{ marginBottom: "8px", fontSize: "13px", color: "#4a5568" }}>
                                            Use the date filters below to see changes in statistics over a period of time. Changes are displayed in parenthesis ( ).
                                        </div>
                                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px" }}>
                                            <label style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                                                <span>Period:</span>
                                                <select
                                                    className="form-control"
                                                    value={statsRangePreset}
                                                    onChange={(e) => applyStatsRangePreset(e.target.value)}
                                                    style={{ width: "auto", minWidth: "140px", padding: "4px 8px" }}
                                                >
                                                    <option value="last-week">Last week</option>
                                                    <option value="last-month">Last month</option>
                                                    <option value="last-year">Last year</option>
                                                    <option value="custom">Custom</option>
                                                </select>
                                            </label>

                                            {statsRangePreset === "custom" && (
                                                <>
                                                    <span>Analysis Date:</span>
                                                    <input
                                                        type="date"
                                                        value={snapshotDate}
                                                        onChange={(e) => setSnapshotDate(e.target.value)}
                                                        style={{ padding: "4px 8px", border: "1px solid #cbd5e0", borderRadius: "4px" }}
                                                    />
                                                    <div style={{ border: "1px solid #ccc", borderRadius: "10px", padding: "8px 16px", display: "flex", alignItems: "center", backgroundColor: "aliceblue", gap: "8px" }}>
                                                        <label style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={compareEnabled}
                                                                onChange={(e) => setCompareEnabled(e.target.checked)}
                                                            />
                                                            Display changes since:
                                                        </label>
                                                        <input
                                                            type="date"
                                                            value={compareDate}
                                                            disabled={!compareEnabled}
                                                            onChange={(e) => setCompareDate(e.target.value)}
                                                            style={{ padding: "4px 8px", border: "1px solid #cbd5e0", borderRadius: "4px" }}
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {isUpdatingStats ? (
                                                <div style={{ display: "inline-flex", alignItems: "center", minHeight: "34px", padding: "0 12px" }}>
                                                    <LoadingSpinnerImage small={true} />
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    style={{ backgroundColor: "#3b9c98", borderColor: "#3b9c98" }}
                                                    onClick={startStatsUpdate}
                                                >
                                                    <i className="fas fa-sync-alt" style={{ marginRight: "8px" }} />
                                                    Update Statistics
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {profileTypes.map((type) => (
                                    <section
                                        key={type}
                                        className="watchlists-details-section"
                                        ref={(node) => setSectionRef(`type:${type}`, node)}
                                        id={`watchlist-section-type-${type}`}
                                    >
                                        <div className="watchlists-details-section-header">
                                            <h3 className="watchlists-details-section-title">{getProfileTypeLabel(type)}</h3>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                title="Table configuration"
                                                onClick={(e) => toggleSectionMenu(type, e)}
                                                disabled={isUpdatingTypes}
                                                style={{ minWidth: "32px" }}
                                            >
                                                <i className="fas fa-cog" />
                                            </button>
                                        </div>
                                        <LayoutComponents.InfoBlock>
                                            <ProfileTypeTab
                                                key={`${type}-${profilesRefreshKey}`}
                                                workspaceId={AppContext.currentItem.Id}
                                                profileType={type}
                                                snapshotDate={snapshotDate}
                                                compareDate={compareDate}
                                                compareEnabled={compareEnabled}
                                                statsVersion={statsVersion}
                                                onStatsUpdateComplete={handleStatsUpdateComplete}
                                                onProfileTypeEmptied={handleProfileTypeEmptied}
                                                columnsDialogOpen={columnsDialogType === type}
                                                onColumnsDialogClose={() => setColumnsDialogType(null)}
                                            />
                                        </LayoutComponents.InfoBlock>
                                    </section>
                                ))}

                                <section
                                    className="watchlists-details-section"
                                    ref={(node) => setSectionRef("notes", node)}
                                    id="watchlist-section-notes"
                                >
                                    <h3 className="watchlists-details-section-title">Intel Blocks</h3>
                                    <LayoutComponents.InfoBlock>
                                        <NotesTab workspaceId={AppContext.currentItem.Id} />
                                    </LayoutComponents.InfoBlock>
                                </section>

                                <section
                                    className="watchlists-details-section"
                                    ref={(node) => setSectionRef("systematic-reviews", node)}
                                    id="watchlist-section-systematic-reviews"
                                >
                                    <h3 className="watchlists-details-section-title">Systematic Reviews</h3>
                                    <LayoutComponents.InfoBlock>
                                        <SystematicReviewsTab workspaceId={AppContext.currentItem.Id} />
                                    </LayoutComponents.InfoBlock>
                                </section>

                                <div
                                    className="watchlists-details-scroll-spacer"
                                    style={{ height: bottomSpacerHeight }}
                                    aria-hidden="true"
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>

            <RowActionMenu open={sectionMenuType != null} anchorEl={sectionMenuAnchor} onClose={closeSectionMenu} minWidth={200}>
                {(getProfileTypeConfig(sectionMenuType)?.statsFields || []).length > 0 && (
                    <>
                        <button type="button" className="watchlists-row-action-menu-item" onClick={() => openColumnsDialog(sectionMenuType)}>
                            Configure columns
                        </button>
                        <div className="watchlists-row-action-menu-divider" />
                    </>
                )}
                <button type="button" className="watchlists-row-action-menu-item is-danger" onClick={() => askRemoveProfileType(sectionMenuType)}>
                    Remove group
                </button>
            </RowActionMenu>

            {pendingRemoveType && (
                <ConfirmRemovePopup
                    title="Remove profile type"
                    confirmLabel="Remove"
                    isRemoving={isUpdatingTypes}
                    error={removeTypeError}
                    onCancel={() => { if (!isUpdatingTypes) setPendingRemoveType(null); }}
                    onConfirm={removeProfileType}
                    message={(
                        <>
                            Are you sure you want to remove this profile type from the watchlist:
                            <br />
                            <br />
                            "{getProfileTypeLabel(pendingRemoveType)}"
                            <br />
                            <br />
                            All of its items will be removed from this watchlist.
                        </>
                    )}
                />
            )}

            {AppContext.currentItem?.Id && (
                <AddProfilesPopup
                    isOpen={isAddProfilesOpen}
                    onClose={() => setIsAddProfilesOpen(false)}
                    workspaceId={AppContext.currentItem.Id}
                    enabledProfileTypes={profileTypes}
                    onProfilesChanged={handleProfilesChanged}
                />
            )}
        </div>
    );
};

export default ItemDetailsPane;
